import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FileInputComponent} from "ngx-material-file-input";
import {FormBuilder, FormControl, FormGroup, Validators, FormArray} from "@angular/forms";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormHelperService} from "../../../shared/services/form-helper.service";
import {DOCUMENT} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {DictService} from "../../../shared/services/dict.service";
import {AuthService} from "../../../shared/services/auth.service";
import {FileBase64} from "../../../shared/components/file-drag-and-drop/file-drag-and-drop.component";
import {delay, first, takeUntil} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {CompetitionImportService} from "../../../shared/services/competition-import.service";
import {CompetitionImport} from "../../../shared/interface/competition-import";
import {Uuid} from "../../../shared/interface/response/uuid";
import {ImportPreviewColumn} from "../../../shared/interface/import-preview-column";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {MatDialog} from "@angular/material/dialog";

@Component({
  selector: 'app-competition-data-import',
  templateUrl: './competition-data-import.component.html',
  styleUrls: ['./competition-data-import.component.scss']
})
export class CompetitionDataImportComponent implements OnInit, OnDestroy {

  @ViewChild('fileInput') fileInputComponent: FileInputComponent;

  competitionImportForm: FormGroup;
  competitionImportFormValid = false;

  mappingForm: FormGroup;
  mappingFormValid = false;

  fileMaxSize = 5242880 * 20;
  processingRequest = false;
  formErrors = {};
  userPermissions: ReplaySubject<UserPermissions>;
  competitionImport?: CompetitionImport;
  previewColumns: ImportPreviewColumn[] = [];
  ignoreNotMatchedFields: FormControl;
  typeControl: FormControl;
  ofwcaControl: FormControl;
  partnerControl: FormControl;
  imported = false;
  progressBarDelete: string;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private competitionImportService: CompetitionImportService,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private dictService: DictService,
    private auth: AuthService,
    private dialog: MatDialog,
    private route: ActivatedRoute
  ) {
    this.userPermissions = auth.getUserPermissions();

    this.competitionImportForm = fb.group({
      name: fb.control('', [Validators.required]),
      fileContent: fb.control('', [Validators.required]),
      competitionName: fb.control('', [Validators.required]),
      type: this.typeControl = fb.control('', [Validators.required]),
      ofwca: this.ofwcaControl = fb.control(false),
      partner: this.partnerControl = fb.control(false)
    });

    this.mappingForm = fb.group({
      competitionName: fb.control('', [Validators.required]),
      mappings: fb.array([])
    });

    this.ignoreNotMatchedFields = fb.control(false);
  }

  ngOnInit(): void {
    this.competitionImportForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      )
      .subscribe(() => {
        this.competitionImportFormValid = this.competitionImportForm.valid;
      });

    this.mappingForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      )
      .subscribe(() => {
        this.mappingFormValid = this.mappingForm.valid;
      });

    this.typeControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((type: string) => {
        if (type === 'ofwca') {
          this.ofwcaControl.setValue(true);
          this.partnerControl.setValue(false);
        } else if (type === 'partner') {
          this.ofwcaControl.setValue(false);
          this.partnerControl.setValue(true);
        } else {
          this.ofwcaControl.setValue(false);
          this.partnerControl.setValue(false);
        }
      });

    this.route.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe(params => {
        if (params.importId) {
          this.loadImport(params.importId);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get mappingsArray(): FormArray {
    return this.mappingForm.get('mappings') as FormArray;
  }

  fileSelected(file: FileBase64): void {
    this.competitionImportForm.get('fileContent').setValue(file?.base64);
    this.competitionImportForm.get('name').setValue(file?.filename);
  }

  import(): void {
    if (!this.competitionImportForm.valid) {
      this.competitionImportForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }
    if (this.processingRequest) {
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.competitionImportForm, this.formErrors);
    this.formErrors = {};
    const formData = {...this.competitionImportForm.value};

    this.competitionImportService.import(formData)
      .pipe(first())
      .subscribe((response: Uuid) => {
        this.snack.open('Rozpoczynanie importowania danych', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.loadImport(response.uuid);
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open('Podczas tworzenia importu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.competitionImportForm, this.formErrors);
        }
      });
  }

  loadImport(importId: string): void {
    this.competitionImportService.getImport(importId)
      .pipe(delay(1000))
      .subscribe((competitionImport: CompetitionImport) => {
        this.competitionImport = competitionImport;

        this.competitionImportForm.reset({
          name: competitionImport.name,
          competitionName: competitionImport.competitionName
        });
        this.typeControl.setValue(
          this.competitionImport.ofwca ? 'ofwca' : this.competitionImport.partner ? 'partner' : ''
        );

        this.buildPreview();
        this.imported = true;
        this.snack.open('Dane konfiguracji zostały wczytane', '', {
          duration: 2000,
        });
      });
  }

  buildPreview(): void {
    if (!this.competitionImport?.importColumns || this.competitionImport.importColumns.length === 0) {
      this.previewColumns = [];
      this.mappingsArray.clear();
      return;
    }

    const map = new Map<string, ImportPreviewColumn>();
    this.mappingsArray.clear();

    this.mappingForm.reset({
      competitionName: this.competitionImportForm.get('competitionName').value
    });

    const sortedRows = [...this.competitionImport.importColumns].sort((a, b) => {
      return a.orderId - b.orderId;
    });

    sortedRows.forEach((row: any) => {
      const normalizedValues = row.sampleValues.map((value: any) =>
        (value === '' || value === undefined) ? null : value
      );

      const control = this.fb.group({
        sourceColumn: [row.headerName, Validators.required],
        values: [normalizedValues],
        targetField: [row.additionalField?.uuid || null]
      });

      this.mappingsArray.push(control);

      const previewColumn: ImportPreviewColumn = {
        matched: !!row.additionalField,
        column: row.headerName,
        values: normalizedValues,
        control
      };

      map.set(row.headerName, previewColumn);
    });

    this.previewColumns = sortedRows.map(row => map.get(row.headerName)!);
  }

  sendForm(): void {
    if (!this.mappingForm.valid) {
      this.mappingForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola mapowania', '', {
        duration: 2000,
      });
      return;
    }

    if (this.processingRequest || !this.competitionImport) {
      return;
    }

    const mappings = this.mappingsArray.value;

    const payload = {
      competitionName: this.mappingForm.get('competitionName').value,
      importId: this.competitionImport.uuid,
      columnMappings: mappings.map((mapping: any) => ({
        sourceColumn: mapping.sourceColumn,
        targetFieldId: mapping.targetField
      })),
      ignoreUnmapped: this.ignoreNotMatchedFields.value
    };

    this.processingRequest = true;

    this.competitionImportService.createFromImport(payload)
      .pipe(first())
      .subscribe({
        next: () => {
          this.processingRequest = false;
          this.snack.open('Dane konkursu zostały zapisane pomyślnie', '', {
            duration: 2000
          });
          this.router.navigate(['/wyniki-konkursow/lista-importow']);
        },
        error: (error: HttpErrorResponse) => {
          this.processingRequest = false;
          this.snack.open('Błąd podczas zapisywania danych', error.status.toString(), {
            duration: 2000
          });

          if (error.status === 400) {
            this.formErrors = error.error.error.list;
            this.formHelper.setBackendErrorState(this.mappingForm, this.formErrors);
          }
        }
      });
  }

  deleteImport(competitionImport: CompetitionImport): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz anulować?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.progressBarDelete = competitionImport.uuid;
        this.competitionImportService.deleteImport(competitionImport.uuid)
          .subscribe((response: Uuid) => {
            const dialogRefv1 = this.dialog.open(JobResultDialogComponent, {
              disableClose: true,
              width: '500px',
              data: {jobId: response.uuid, showProgress: false},
            });

            dialogRefv1.afterClosed().subscribe(() => {
              this.snack.open('Pomyślnie usunięto import', '', {
                duration: 2000,
              });

              const currentUrl = this.router.url;
              this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
                this.router.navigate([currentUrl]);
              });
            });
          }, (error: HttpErrorResponse) => {
            this.snack.open('Przy usuwaniu pliku wystąpił błąd', error.status.toString(), {duration: 4000});
            if (error.status === 400) {
              this.snack.open(error.error.error.text, '400', {duration: 4000});
            }
          });
      }
    });
  }
}
